import { GetterTree, ActionTree, MutationTree, ActionContext } from 'vuex';
import { BookingType } from '@/models/booking-type.model';
import { BookingStatus } from '@/models/booking-status.model';
import { PaymentStatus } from '@/models/payment-status.model';
import { PaymentType } from '@/models/payment-type.model';
import { GuestType } from '@/models/guest-type.model';
import { EventStatus } from '@/models/event-status.model';
import { EventType } from '@/models/event-type.model';
import { Gender } from '@/models/gender.model';
import { PaymentMethod } from '@/models/payment-method.model';
import { PhoneType } from '@/models/phone-type.model';
import { DietaryRequirement } from '@/models/dietary-requirement.model';
import { AddressType } from '@/models/address-type.model';
import { Location } from '@/models/location.model';
import { ContactType } from '@/models/contact-type.model';
import { Tag } from '@/models/tag.model';
import { Role } from '@/models/role.model';
import { Permission, UserPermissions } from '@/models/permission.model';
import { cloneDeep } from 'lodash';
import store from '@/store';
import { ability } from '@/main';

interface LoadingState {
  bookingTypes: BookingType[] | null;
  bookingStatuses: BookingStatus[] | null;
  paymentStatuses: PaymentStatus[] | null;
  paymentTypes: PaymentType[] | null;
  guestTypes: GuestType[] | null;
  eventStatuses: EventStatus[] | null;
  eventTypes: EventType[] | null;
  genders: Gender[] | null;
  paymentMethods: PaymentMethod[] | null;
  phoneTypes: PhoneType[] | null;
  dietaryRequirements: DietaryRequirement[] | null;
  addressTypes: AddressType[] | null;
  locations: Location[] | null;
  centres: Location[] | null;
  contactTypes: ContactType[] | null;
  tags: Tag[] | null;
  roles: Role[] | null;
  permissions: Permission[] | null;
}

export const defaultStaticListingsState: LoadingState = {
  bookingTypes: null,
  bookingStatuses: null,
  paymentStatuses: null,
  paymentTypes: null,
  guestTypes: null,
  eventStatuses: null,
  eventTypes: null,
  genders: null,
  paymentMethods: null,
  phoneTypes: null,
  dietaryRequirements: null,
  addressTypes: null,
  locations: null,
  centres: null,
  contactTypes: null,
  tags: null,
  roles: null,
  permissions: null
};

const state: LoadingState = cloneDeep(defaultStaticListingsState);

const mutations: MutationTree<LoadingState> = {
  ADD_BOOKING_TYPES(returnedState: LoadingState, bookingTypes: BookingType[]): void {
    returnedState.bookingTypes = bookingTypes;
  },
  ADD_BOOKING_STATUSES(returnedState: LoadingState, bookingStatuses: BookingStatus[]): void {
    returnedState.bookingStatuses = bookingStatuses;
  },
  ADD_PAYMENT_STATUSES(returnedState: LoadingState, paymentStatuses: PaymentStatus[]): void {
    returnedState.paymentStatuses = paymentStatuses;
  },
  ADD_PAYMENT_TYPES(returnedState: LoadingState, paymentTypes: PaymentType[]): void {
    returnedState.paymentTypes = paymentTypes;
  },
  ADD_GUEST_TYPES(returnedState: LoadingState, guestTypes: GuestType[]): void {
    returnedState.guestTypes = guestTypes;
  },
  ADD_EVENT_STATUSES(returnedState: LoadingState, eventStatuses: EventStatus[]): void {
    returnedState.eventStatuses = eventStatuses;
  },
  ADD_EVENT_TYPES(returnedState: LoadingState, eventTypes: EventType[]): void {
    returnedState.eventTypes = eventTypes;
  },
  ADD_GENDERS(returnedState: LoadingState, genders: Gender[]): void {
    returnedState.genders = genders;
  },
  ADD_PAYMENT_METHODS(returnedState: LoadingState, paymentMethods: PaymentMethod[]): void {
    returnedState.paymentMethods = paymentMethods;
  },
  ADD_PHONE_TYPES(returnedState: LoadingState, phoneTypes: PhoneType[]): void {
    returnedState.phoneTypes = phoneTypes;
  },
  ADD_DIETARY_REQUIREMENTS(returnedState: LoadingState, dietaryRequirements: DietaryRequirement[]): void {
    returnedState.dietaryRequirements = dietaryRequirements;
  },
  ADD_ADDRESS_TYPES(returnedState: LoadingState, addressTypes: AddressType[]): void {
    returnedState.addressTypes = addressTypes;
  },
  ADD_LOCATIONS(returnedState: LoadingState, locations: Location[]): void {
    returnedState.locations = locations;
  },
  ADD_CENTRES(returnedState: LoadingState, centres: Location[]): void {
    returnedState.centres = centres;
  },
  ADD_CONTACT_TYPES(returnedState: LoadingState, contactTypes: ContactType[]): void {
    returnedState.contactTypes = contactTypes;
  },
  ADD_TAGS(returnedState: LoadingState, tags: Tag[]): void {
    returnedState.tags = tags;
  },
  ADD_ROLES(returnedState: LoadingState, roles: Role[]): void {
    returnedState.roles = roles;
  },
  ADD_PERMISSIONS(returnedState: LoadingState, permissions: Permission[]): void {
    returnedState.permissions = permissions;
  }
};

const actions: ActionTree<LoadingState, {}> = {
  addBookingTypes: ({ commit }: ActionContext<LoadingState, {}>, payload: BookingType[]): void => {
    commit('ADD_BOOKING_TYPES', payload);
  },
  addBookingStatuses: ({ commit }: ActionContext<LoadingState, {}>, payload: BookingStatus[]): void => {
    commit('ADD_BOOKING_STATUSES', payload);
  },
  addPaymentStatuses: ({ commit }: ActionContext<LoadingState, {}>, payload: PaymentStatus[]): void => {
    commit('ADD_PAYMENT_STATUSES', payload);
  },
  addPaymentTypes: ({ commit }: ActionContext<LoadingState, {}>, payload: PaymentType[]): void => {
    commit('ADD_PAYMENT_TYPES', payload);
  },
  addGuestTypes: ({ commit }: ActionContext<LoadingState, {}>, payload: GuestType[]): void => {
    commit('ADD_GUEST_TYPES', payload);
  },
  addEventStatuses: ({ commit }: ActionContext<LoadingState, {}>, payload: EventStatus[]): void => {
    commit('ADD_EVENT_STATUSES', payload);
  },
  addEventTypes: ({ commit }: ActionContext<LoadingState, {}>, payload: EventType[]): void => {
    commit('ADD_EVENT_TYPES', payload);
  },
  addGenders: ({ commit }: ActionContext<LoadingState, {}>, payload: Gender[]): void => {
    commit('ADD_GENDERS', payload);
  },
  addPaymentMethods: ({ commit }: ActionContext<LoadingState, {}>, payload: PaymentMethod[]): void => {
    commit('ADD_PAYMENT_METHODS', payload);
  },
  addPhoneTypes: ({ commit }: ActionContext<LoadingState, {}>, payload: PhoneType[]): void => {
    commit('ADD_PHONE_TYPES', payload);
  },
  addDietaryRequirements: ({ commit }: ActionContext<LoadingState, {}>, payload: DietaryRequirement[]): void => {
    commit('ADD_DIETARY_REQUIREMENTS', payload);
  },
  addAddressTypes: ({ commit }: ActionContext<LoadingState, {}>, payload: AddressType[]): void => {
    commit('ADD_ADDRESS_TYPES', payload);
  },
  addLocations: ({ commit }: ActionContext<LoadingState, {}>, payload: Location[]): void => {
    commit('ADD_LOCATIONS', payload);
  },
  addCentres: ({ commit }: ActionContext<LoadingState, {}>, payload: Location[]): void => {
    commit('ADD_CENTRES', payload);
  },
  addContactTypes: ({ commit }: ActionContext<LoadingState, {}>, payload: ContactType[]): void => {
    commit('ADD_CONTACT_TYPES', payload);
  },
  addTags: ({ commit }: ActionContext<LoadingState, {}>, payload: Tag[]): void => {
    commit('ADD_TAGS', payload);
  },
  addRoles: ({ commit }: ActionContext<LoadingState, {}>, payload: Role[]): void => {
    commit('ADD_ROLES', payload);
  },
  addPermissions: ({ commit }: ActionContext<LoadingState, {}>, payload: Permissions[]): void => {
    commit('ADD_PERMISSIONS', payload);
  }
};

export enum ListingsStoreGetters {
  bookingTypes = 'bookingTypes',
  bookingStatuses = 'bookingStatuses',
  paymentStatuses = 'paymentStatuses',
  paymentTypes = 'paymentTypes',
  guestTypes = 'guestTypes',
  eventStatuses = 'eventStatuses',
  eventTypes = 'eventTypes',
  genders = 'genders',
  paymentMethods = 'paymentMethods',
  phoneTypes = 'phoneTypes',
  dietaryRequirements = 'dietaryRequirements',
  addressTypes = 'addressTypes',
  locations = 'locations',
  centres = 'centres',
  tags = 'tags',
  contactTypes = 'contactTypes',
  roles = 'roles',
  permissions = 'permissions',
  hasMissingListing = 'hasMissingListing'
}

const getters: GetterTree<LoadingState, {}> = {
  [ListingsStoreGetters.bookingTypes]: (returnedState: LoadingState): BookingType[] | null => {
    return returnedState.bookingTypes;
  },
  [ListingsStoreGetters.bookingStatuses]: (returnedState: LoadingState): BookingStatus[] | null => {
    return returnedState.bookingStatuses;
  },
  [ListingsStoreGetters.paymentStatuses]: (returnedState: LoadingState): PaymentStatus[] | null => {
    return returnedState.paymentStatuses;
  },
  [ListingsStoreGetters.paymentTypes]: (returnedState: LoadingState): PaymentType[] | null => {
    return returnedState.paymentTypes;
  },
  [ListingsStoreGetters.guestTypes]: (returnedState: LoadingState): GuestType[] | null => {
    return returnedState.guestTypes;
  },
  [ListingsStoreGetters.eventStatuses]: (returnedState: LoadingState): EventStatus[] | null => {
    return returnedState.eventStatuses;
  },
  [ListingsStoreGetters.eventTypes]: (returnedState: LoadingState): EventType[] | null => {
    return returnedState.eventTypes;
  },
  [ListingsStoreGetters.genders]: (returnedState: LoadingState): Gender[] | null => {
    return returnedState.genders;
  },
  [ListingsStoreGetters.paymentMethods]: (returnedState: LoadingState): PaymentMethod[] | null => {
    return returnedState.paymentMethods;
  },
  [ListingsStoreGetters.phoneTypes]: (returnedState: LoadingState): PhoneType[] | null => {
    return returnedState.phoneTypes;
  },
  [ListingsStoreGetters.dietaryRequirements]: (returnedState: LoadingState): DietaryRequirement[] | null => {
    return returnedState.dietaryRequirements;
  },
  [ListingsStoreGetters.addressTypes]: (returnedState: LoadingState): AddressType[] | null => {
    return returnedState.addressTypes;
  },
  [ListingsStoreGetters.locations]: (returnedState: LoadingState): Location[] | null => {
    const allLocations: Location[] = (returnedState.locations ?? []) as Location[];
    const canViewAllLocations: boolean =
      ability.can(UserPermissions.manage_all) || ability.can(UserPermissions.access_all_locations);

    if (canViewAllLocations) {
      return allLocations;
    }

    const myViewLocationsIds: string[] = (store.getters.myViewLocations ?? []).map(
      (centre: Location): string => centre.id
    );
    return allLocations.filter((location: Location): boolean => myViewLocationsIds.includes(location.id));
  },
  [ListingsStoreGetters.centres]: (returnedState: LoadingState): Location[] | null => {
    return returnedState.centres;
  },
  [ListingsStoreGetters.contactTypes]: (returnedState: LoadingState): ContactType[] | null => {
    return returnedState.contactTypes;
  },
  [ListingsStoreGetters.tags]: (returnedState: LoadingState): Tag[] | null => {
    return returnedState.tags;
  },
  [ListingsStoreGetters.roles]: (returnedState: LoadingState): Role[] | null => {
    return returnedState.roles;
  },
  [ListingsStoreGetters.permissions]: (returnedState: LoadingState): Permission[] | null => {
    return returnedState.permissions;
  },
  [ListingsStoreGetters.hasMissingListing]: (returnedState: LoadingState): boolean => {
    return Object.values(returnedState).some((item: any[] | null): boolean => !item);
  }
};

export default {
  state,
  mutations,
  actions,
  getters
};
